const contentBlock = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12
}

const sectionTitle = {
  fontSize: 16,
  fontWeight: 700,
  marginTop: 4
}

const chartWrap = {
  background: '#fff',
  border: '1px solid #d4d4d4',
  borderRadius: 10,
  padding: '12px 10px 6px 10px',
  position: 'relative'
}

const chartInner = {
  position: 'relative',
  width: '99%',
  height: 260
}

const legendWrap = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  marginTop: 6
}

const legendItem = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  background: '#fff',
  border: '1px solid #d4d4d4',
  borderRadius: 8,
  padding: '4px 8px'
}

const legendColor = {
  width: 12,
  height: 12,
  borderRadius: 2,
  display: 'inline-block'
}

const yLabelStyle = {
  position: 'absolute',
  width: 40,
  textAlign: 'right',
  fontSize: 14,
  color: '#666',
  transform: 'translateY(-50%)',
  pointerEvents: 'none'
}

const monthLabelStyle = {
  position: 'absolute',
  transform: 'translateX(-50%)',
  fontSize: 14,
  textTransform: 'capitalize',
  color: '#333',
  pointerEvents: 'none',
  whiteSpace: 'nowrap'
}

const valueLabelStyle = {
  position: 'absolute',
  transform: 'translateX(-50%)',
  fontSize: 13,
  color: '#000',
  pointerEvents: 'none',
  whiteSpace: 'nowrap'
}

const COLORS = ['#4F81BD', '#6FA16F', '#C27C59', '#9B6FAE', '#BFA65A']

const CHART = {
  width: 720,
  height: 260,
  left: 46,
  right: 8,
  top: 12,
  bottom: 40
}

const StatsCostsModal = ({ stats, t, fmtNum, vehicles = [], filterCriteria = {}, i18n }) => {
  const points = stats.costsChartData || []
  const plotWidth = CHART.width - CHART.left - CHART.right
  const plotHeight = CHART.height - CHART.top - CHART.bottom
  const ySteps = 5

  const maxCost = Math.max(...points.map((item) => Number(item.cost || 0)), 1)
  const roundedMaxCost = Math.max(Math.ceil((maxCost * 1.1) / 100) * 100, 100)
  const stepX = points.length > 1 ? plotWidth / (points.length - 1) : 0

  const getY = (cost) => CHART.top + plotHeight - ((Number(cost || 0) / roundedMaxCost) * plotHeight)
  const getX = (index) => CHART.left + (index * stepX)

  const xToPercent = (x) => (x / CHART.width) * 100
  const yToPercent = (y) => (y / CHART.height) * 100

  const chartPoints = points
    .map((item, index) => `${getX(index)},${getY(item.cost)}`)
    .join(' ')

  const trendPoints = points.map((item, index) => ({
    x: index,
    y: Number(item.cost || 0)
  }))

  const trendLine = (() => {
    if (trendPoints.length < 2) return ''

    const n = trendPoints.length
    const sumX = trendPoints.reduce((acc, p) => acc + p.x, 0)
    const sumY = trendPoints.reduce((acc, p) => acc + p.y, 0)
    const sumXY = trendPoints.reduce((acc, p) => acc + (p.x * p.y), 0)
    const sumX2 = trendPoints.reduce((acc, p) => acc + (p.x ** 2), 0)
    const denominator = (n * sumX2) - (sumX ** 2)

    if (denominator === 0) return ''

    const slope = ((n * sumXY) - (sumX * sumY)) / denominator
    const intercept = (sumY - (slope * sumX)) / n

    const startY = getY(intercept)
    const endY = getY((slope * (n - 1)) + intercept)

    return `${getX(0)},${startY} ${getX(n - 1)},${endY}`
  })()

  const statsLocale = i18n?.language === 'ua' ? 'uk' : (i18n?.language || 'sk')
  const labels = points.map((item) => {
    const d = new Date(`${item.month}-01`)
    return d.toLocaleString(statsLocale, { month: 'short' })
  })

  const selectedVehicle = filterCriteria?.vehicleId
    ? vehicles.find((vehicle) => String(vehicle.id) === String(filterCriteria.vehicleId))
    : null

  const vehicleText = selectedVehicle
    ? `${selectedVehicle.name || selectedVehicle.model || ''}${selectedVehicle.licensePlate ? ` (${selectedVehicle.licensePlate})` : ''}`
    : t('TRIPLOG.STATISTICS.ALL_VEHICLES', { defaultValue: 'všetky vozidlá' })

  const getColor = (vehicleId) => {
    const idx = vehicles.findIndex((v) => String(v.id) === String(vehicleId))
    return COLORS[idx % COLORS.length]
  }

  return (
    <div style={contentBlock}>
      <div style={sectionTitle}>
        {t('TRIPLOG.STATISTICS.COSTS.TITLE')} <span style={{ color: '#107c6b' }}>{vehicleText}</span>
      </div>

      <div style={chartWrap}>
        <div style={chartInner}>
          <svg
            width="100%"
            height={CHART.height}
            viewBox={`0 0 ${CHART.width} ${CHART.height}`}
            preserveAspectRatio="none"
          >
            {Array.from({ length: ySteps + 1 }, (_, i) => {
              const value = (roundedMaxCost / ySteps) * i
              const y = getY(value)

              return (
                <g key={value}>
                  <line
                    x1={CHART.left}
                    y1={y}
                    x2={CHART.width - CHART.right}
                    y2={y}
                    stroke="#e8e8e8"
                  />
                </g>
              )
            })}

            <line
              x1={CHART.left}
              y1={CHART.top + plotHeight}
              x2={CHART.width - CHART.right}
              y2={CHART.top + plotHeight}
              stroke="#d5d5d5"
            />

            {trendLine && (
              <polyline
                fill="none"
                stroke="#73d6c9"
                strokeWidth="2"
                strokeDasharray="6 4"
                points={trendLine}
              />
            )}

            <polyline
              fill="none"
              stroke="#107c6b"
              strokeWidth="3"
              points={chartPoints}
            />

		{points.map((item, index) => {
 		 const x = getX(index)
 		 const y = getY(item.cost)

 		 const xAxisY = CHART.top + plotHeight

 		 return (
  		  <g key={item.month}>
   		   {/* vertikálna prerušovaná čiara */}
   		   <line
     		   x1={x}
     		   y1={xAxisY}
      		  x2={x}
      		  y2={y}
     		   stroke="#bdbdbd"
      		  strokeWidth="1"
      		  strokeDasharray="4 4"
     		 />

    		  <circle
     		   cx={x}
     		   cy={y}
     		   r="4"
     		   fill={selectedVehicle ? getColor(selectedVehicle.id) : '#107c6b'}
    		  />
   		 </g>
 		 )
		})}
          </svg>

          {Array.from({ length: ySteps + 1 }, (_, i) => {
            const value = (roundedMaxCost / ySteps) * i
            const y = getY(value)

            return (
              <div
                key={`ylabel-${value}`}
                style={{
                  ...yLabelStyle,
                  left: 0,
                  top: `${yToPercent(y)}%`
                }}
              >
                {fmtNum(value, 0)}
              </div>
            )
          })}

          {points.map((item, index) => {
            const x = getX(index)
            const y = getY(item.cost)

            return (
              <div key={`labels-${item.month}`}>
                <div
                  style={{
                    ...monthLabelStyle,
                    left: `${xToPercent(x)}%`,
                    top: `${yToPercent(CHART.height - 24)}%`
                  }}
                >
                  {labels[index]}
                </div>

                <div
                  style={{
                    ...valueLabelStyle,
                    left: `${xToPercent(x)}%`,
                    top: `${yToPercent(Math.max(CHART.top - 10, y - 25))}%`
                  }}
                >
                  {fmtNum(item.cost, 2)}€
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {vehicles.length > 0 && (
        <div style={legendWrap}>
          {vehicles.map((vehicle) => (
            <div key={vehicle.id} style={legendItem}>
              <span style={{ ...legendColor, backgroundColor: getColor(vehicle.id) }} />
              <span>
                {vehicle.name || vehicle.model || ''}
                {vehicle.licensePlate ? ` (${vehicle.licensePlate})` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default StatsCostsModal